import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  TagLabel,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { Messages } from "../../../core/api";
import Select from "../../../shared/components/Select";
import {
  AvailableCounty,
  OfficeBranch,
  OfficeBranchCreateParams,
  OfficeBranchEditParams,
} from "../office-branch.types";
import { OfficeBranchId, OfficeId } from "../../../shared/schema/schema";
import AddressAutocompleteInput from "./AddressAutocompleteInput";

interface Props {
  assignedCountiesInOffice: Array<{ state: string; countyName: string }>;
  availableCounties: AvailableCounty[];
  branch: OfficeBranch | null;
  isOpen: boolean;
  isSubmitting: boolean;
  offices: Array<{ id: OfficeId; name: string }>;
  onClose: () => void;
  onCreate: (officeId: OfficeId, params: OfficeBranchCreateParams) => void;
  onEdit: (officeId: OfficeId, branchId: OfficeBranchId, params: OfficeBranchEditParams) => void;
}

type County = {
  state: string;
  countyName: string;
};

const encodeCounty = (c: County): string => {
  return `${c.state}::${c.countyName}`;
};

const decodeCounty = (s: string): County => {
  const [state, countyName] = s.split("::");
  return {
    state,
    countyName,
  };
};

const OfficeBranchModal = (props: Props) => {
  const isCreateMode = props.branch === null;

  const [name, setName] = React.useState("");
  const [licenseNumber, setLicenseNumber] = React.useState("");
  const [counties, setCounties] = React.useState<County[]>([]);
  const [officeId, setOfficeId] = React.useState<OfficeId | null>(null);
  const [addressText, setAddressText] = React.useState<string>("");
  const [addressComponents, setAddressComponents] = React.useState<
    Messages["AddressComponents"] | null
  >(null);

  React.useEffect(() => {
    if (props.isOpen) {
      if (props.branch !== null) {
        setName(props.branch.name);
        setLicenseNumber(props.branch.licenseNumber ?? "");
        setCounties(
          props.branch.counties.map((c) => ({ state: c.state, countyName: c.countyName }))
        );
        setOfficeId(props.branch.officeId);
        setAddressText(props.branch.address);
        setAddressComponents(props.branch.addressComponents);
      } else {
        setName("");
        setLicenseNumber("");
        setCounties([]);
        setOfficeId(props.offices.length > 0 ? props.offices[0].id : null);
        setAddressText("");
        setAddressComponents(null);
      }
    }
  }, [props.branch, props.isOpen, props.offices]);

  const handleAddressSelected = (address: string, components: Messages["AddressComponents"]) => {
    setAddressText(address);
    setAddressComponents(components);
  };

  const handleAddressClear = () => {
    setAddressText("");
    setAddressComponents(null);
  };

  const handleSubmit = () => {
    if (name.trim() === "" || addressComponents === null) return;

    const params = {
      address: addressText,
      addressComponents,
      counties,
      licenseNumber: licenseNumber.trim() === "" ? null : licenseNumber.trim(),
      name: name.trim(),
    };

    if (props.branch === null) {
      if (officeId === null) return;
      props.onCreate(officeId, params as OfficeBranchCreateParams);
    } else {
      props.onEdit(props.branch.officeId, props.branch.id, params as OfficeBranchEditParams);
    }
  };

  const canSubmit =
    name.trim() !== "" &&
    addressComponents !== null &&
    (!isCreateMode || officeId !== null) &&
    !props.isSubmitting;

  return (
    <Modal isOpen={props.isOpen} size="md" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isCreateMode ? "New Office Branch" : "Edit Office Branch"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            {isCreateMode && (
              <FormControl isRequired>
                <FormLabel>Office</FormLabel>
                <Select
                  label="Office"
                  multiple={false}
                  options={props.offices.map((office) => ({
                    label: office.name,
                    value: office.id,
                  }))}
                  value={officeId}
                  onChange={(office) => {
                    setOfficeId(office ?? null);
                  }}
                />
              </FormControl>
            )}

            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                placeholder="Branch name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>License Number</FormLabel>
              <Input
                placeholder="License number (optional)"
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Address</FormLabel>
              <AddressAutocompleteInput
                addressText={addressText}
                value={addressComponents}
                onChange={handleAddressSelected}
                onClear={handleAddressClear}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Counties</FormLabel>
              <Select
                multiple
                searchable
                label="Counties"
                options={props.availableCounties.map((county) => ({
                  label: `${county.countyName}, ${county.state}`,
                  value: encodeCounty(county),
                }))}
                value={counties.map(encodeCounty)}
                onChange={(selected) => {
                  setCounties(selected?.map(decodeCounty) ?? []);
                }}
              />
              <Box>
                {counties.map((selectedCounty) => (
                  <Tag
                    key={`${selectedCounty.countyName}, ${selectedCounty.state}`}
                    colorScheme="blue"
                    m={1}
                    size="md"
                  >
                    <TagLabel>
                      {selectedCounty.countyName}, {selectedCounty.state}
                    </TagLabel>
                  </Tag>
                ))}
              </Box>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button isDisabled={props.isSubmitting} mr={3} variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!canSubmit}
            isLoading={props.isSubmitting}
            onClick={handleSubmit}
          >
            {isCreateMode ? "Create" : "Save"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OfficeBranchModal;
